import { injectable } from "inversify";
import StablishmentRepository from "../../../domain/repositories/StablishmentRepository";
import { Either, right, left } from "fp-ts/lib/Either";
import ErrorEntity from "../../../domain/entities/ErrorEntity";
import StablishmentEntity from "../../../domain/entities/StablishmentEntity";
import Testing from "../../../presentation/utils/Testing";
import SearchResultEntity from "../../../domain/entities/SearchResultEntity";
import ScheduleEntity, { ScheduleDay } from "../../../domain/entities/ScheduleEntity";

const fakeStablishment: StablishmentEntity = {
    id: "123",
    name: "Test Stablishment",
    address: "123 Test Street",
    latitude: 40.7128,
    longitude: -74.0060,
    phone: "+1-555-0001",
    email: "email@example.com",
    type: "restaurant",
    managerName: "Manager Name",
    supervisor: {
        id: "supervisor-123",
        name: "John Supervisor",
        phone: "+1-555-0100",
        username: "jsupervisor",
        role: "supervisor" as any,
        document: "12345678",
        documentType: "DNI",
        email: "supervisor@example.com"
    },
    schedules: [
        new ScheduleEntity({ id: "sch-1", day: ScheduleDay.lunes, openTime: "09:00", closeTime: "12:00", }),
        new ScheduleEntity({ id: "sch-2", day: ScheduleDay.lunes, openTime: "14:00", closeTime: "18:00", }),
        new ScheduleEntity({ id: "sch-3", day: ScheduleDay.martes, openTime: "09:00", closeTime: "18:00", }),
        new ScheduleEntity({ id: "sch-4", day: ScheduleDay.miercoles, openTime: "10:00", closeTime: "16:00", }),
        new ScheduleEntity({ id: "sch-5", day: ScheduleDay.jueves, openTime: "08:00", closeTime: "14:00", }),
        new ScheduleEntity({ id: "sch-6", day: ScheduleDay.viernes, openTime: "12:00", closeTime: "20:00", }),
        new ScheduleEntity({ id: "sch-7", day: ScheduleDay.sabado, openTime: "10:00", closeTime: "16:00", }),
        new ScheduleEntity({ id: "sch-8", day: ScheduleDay.domingo, openTime: "09:00", closeTime: "13:00", }),
    ]
}

@injectable()
class StablishmentRepositoryFake implements StablishmentRepository {
    getSchedulesByStablishmentAndDay = async (stablishmentId: string, day: Date): Promise<Either<ErrorEntity, ScheduleEntity[]>> => {
        await Testing.sleeper(1000);
        return right(fakeStablishment.schedules);
    }

    async deleteSchedule(id: string): Promise<Either<ErrorEntity, void>> {
        await Testing.sleeper(1000);
        if (id === "cannot-delete-schedule") {
            return left({ code: "CannotDeleteSchedule" });
        }
        return right(undefined);
    }
    async createStablishment(stablishment: StablishmentEntity): Promise<Either<ErrorEntity, StablishmentEntity>> {
        await Testing.sleeper(1000);
        return right({
            ...stablishment,
            id: Math.random().toString(36).substr(2, 9)
        });
    }

    async searchStablishments(_: {
    query?: string, 
    page: number, 
    itemsPerPage: number
}): Promise<Either<ErrorEntity, SearchResultEntity<StablishmentEntity>>> {
        await Testing.sleeper(1000);
        const { query, page, itemsPerPage } = _;
        const fakeResults: StablishmentEntity[] = Array.from({ length: itemsPerPage }, (_, index) => ({
            id: (page * itemsPerPage + index + 1).toString(),
            name: `Stablishment ${page * itemsPerPage + index + 1}`,
            address: `${100 + index} Test Street`,
            phone: `+1-555-${String(index).padStart(4, '0')}`,
            latitude: 40.7128 + (Math.random() - 0.5) * 0.1,
            longitude: -74.0060 + (Math.random() - 0.5) * 0.1,
            email: "email@example.com",
            type: "restaurant",
            managerName: "Manager Name",
            supervisor: {
                id: `supervisor-${index}`,
                name: `Supervisor ${index}`,
                phone: `+1-555-01${index.toString().padStart(2, '0')}`,
                username: `supervisor${index}`,
                role: "supervisor" as any,
                document: `1234567${index}`,
                documentType: "DNI",
                email: `supervisor${index}@example.com`
            },
            schedules: [
                new ScheduleEntity({ id: `sch-${index}-1`, day: ScheduleDay.lunes, openTime: "09:00", closeTime: "12:00", }),
                new ScheduleEntity({ id: `sch-${index}-2`, day: ScheduleDay.lunes, openTime: "14:00", closeTime: "18:00", }),
                new ScheduleEntity({ id: `sch-${index}-3`, day: ScheduleDay.martes, openTime: "09:00", closeTime: "18:00", })
            ]
        }));

        return right({
            data: fakeResults,
            total: 100,
            page,
            itemsPerPage,
            pages: Math.ceil(100 / itemsPerPage)
        });
    }

    async getStablishmentById(id: string): Promise<Either<ErrorEntity, StablishmentEntity>> {
        await Testing.sleeper(1000);
        if (id === "not-found") {
            return left({ code: "NotFound" });
        }
        return right({
            ...fakeStablishment,
            id
        });
    }

    async deleteStablishment(id: string): Promise<Either<ErrorEntity, void>> {
        await Testing.sleeper(1000);
        if (id === "cannot-delete") {
            return left({ code: "CannotDelete" });
        }
        return right(undefined);
    }

    async updateStablishment(stablishment: StablishmentEntity): Promise<Either<ErrorEntity, StablishmentEntity>> {
        await Testing.sleeper(1000);
        return right(stablishment);
    }
}

export default StablishmentRepositoryFake;